import React, { FC, Ref, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from '@reach/router';
import styled from 'styled-components';
import { QuestionOption, QuizAnswer, Quiz as QuizType } from 'types/quiz';

import { LoadingPage, PageContainer, Seo } from 'components';
import Header from 'components/Header';
import Progress from './components/Progress';
import Single from './components/Single';
import MultipleAnswer from './components/MultipleAnswer';
import MultipleImage from './components/MultupleImage';
import Encouragement from './components/Encouragement';
import Info from './components/Info';
import Testimonial from './components/Testimonial';
import { mobile, smLaptop, tablet } from 'styles/breakpoints';
import { AppState } from 'state/types';
import { useRouter } from 'apis/history';
import { updateQuizAnswers } from 'state/user/effects';
import Landing from './components/Landing';
import Landing2 from './components/Landing2';
import { ImageQuestion } from './components/ImageQuesiton';
import SliderQuestion from './components/SliderQuestion';
import Scale from './components/Scale';
import QuizInput from './components/QuizInput';
import PreviousMultiple from './components/PreviousMultiple';
import Insert from './components/Insert';
import { useFirstVisitDate, usePageView } from 'utils/hooks';
import Cost from './components/Cost';
import TextAndImgRow from './components/info/TextAndImgRow';
import TextAndImgColumn from './components/info/TextAndImgColumn';
import BlackFridayBanner from '../../../components/BlackFridayBanner';
import Tracking from 'utils/tracking';
import { normalizeStates } from 'utils/localization';

enum QuestionType {
  Single = 'single',
  Multiple = 'multiple',
  MultipleImages = 'multiple_images',
  Encouragement = 'encouragement',
  Testimonial = 'testimonial',
  Info = 'info',
  InfoRow = 'infoRow',
  InfoCol = 'infoColumn',
  Landing = 'landing',
  Landing2 = 'landing2',
  Image = 'image',
  Slider = 'slider',
  Scale = 'scale',
  Input = 'input',
  PreviousMultiple = 'previous_multiple',
  Insert = 'insert',
  Cost = 'cost',
}

const ContentContainer = styled.div<{ questionChanged: boolean }>`
  flex: 1;
  opacity: ${({ questionChanged }) => (questionChanged ? 1 : 0)};
  transition: opacity 0.25s;

  @media ${smLaptop} {
    padding: 0 1rem;
  }

  @media ${tablet} {
    padding: 0 3rem;
  }

  @media ${mobile} {
    padding: 0 1rem;
  }
`;

const HeaderContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Quiz: FC = () => {
  // const [step, setStep] = useState<number>(1);
  const location = useLocation();
  const q = new URLSearchParams(location.search).get('q') ?? '1';
  const [step, setStep] = useState<number>(Number(q) - 1);
  const [prevStep, setPrevStep] = useState<number>(1);
  const [questionChanged, setQuestionChanged] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<QuestionOption[]>([]);
  const [singleChanged, setSingleChange] = useState<boolean>(false);
  const { countdownDiffInMS } = useFirstVisitDate();
  const pageUrl = window.location.pathname + window.location.search;

  useEffect(() => {
    setPrevStep(step + 1);
  }, [step]);

  const questions = useSelector((state: AppState) => state.funnel.questions);
  const geo = useSelector((state: AppState) => state.user.geolocation);
  const quiz_answers = useSelector(
    (state: AppState) => state.user.quiz_answers,
  );
  const user_ip = useSelector((state: AppState) => state.user.user_ip);

  const { goToCalculating, goToQuizQuestion, navigateBack, goToQuiz } =
    useRouter();

  const dispatch = useDispatch();

  const contentContainerRef: Ref<HTMLDivElement> = useRef(null);

  usePageView({
    city: encodeURIComponent(
      geo?.city?.toLowerCase().replace(/[^a-z0-9]/g, '') || '',
    ),
    country: geo?.iso_country?.toLowerCase(),
    state: normalizeStates(geo?.iso_country || '', geo?.iso_state || ''),
    gender: quiz_answers?.gender ? quiz_answers?.gender[0] : null,
  });

  const question = questions[step];

  const quiz = new URLSearchParams(location.search).get('lp') ?? QuizType.Main;

  useEffect(() => {
    setQuestionChanged(true);
    setSingleChange(true);

    let gender = sessionStorage.getItem('genderAnswer');

    if (!gender) {
      return;
    }

    dispatch(updateQuizAnswers({ gender }));
  }, []);

  useEffect(() => {
    if (!questionChanged) {
      return;
    }

    window?.scroll(0, 0);
    contentContainerRef.current?.scroll(0, 0);
  }, [questionChanged]);

  useEffect(() => {
    const question = new URLSearchParams(location.search).get('q');
    if (!question) {
      return setStep(0);
    }

    if (Number(question) <= questions.length && Number(question) > 0) {
      setStep(Number(question) - 1);
    }
  }, [location]);

  const logEvent = (data: { category: string; label?: string }) => {
    Tracking.trackQuizQuestionAnswered({
      event: `Quiz question answered - ${quiz}`,
      category: `${q} - ${data.category}`,
      label: data.label || '',
    });
  };

  const handleNextQuestion = (answer: QuizAnswer) => {
    if (!questionChanged) {
      return;
    }

    Tracking.virtualPageView(
      {
        city: encodeURIComponent(
          geo?.city?.toLowerCase().replace(/[^a-z0-9]/g, '') || '',
        ),
        country: geo?.iso_country?.toLowerCase(),
        state: normalizeStates(geo?.iso_country || '', geo?.iso_state || ''),
        pageUrl: pageUrl,
        pageTitle: window.location.pathname,
        location: geo?.iso_country?.toLocaleLowerCase(),
      },
      {
        city: encodeURIComponent(
          geo?.city?.toLowerCase().replace(/[^a-z0-9]/g, '') || '',
        ),
        country: geo?.iso_country?.toLowerCase(),
        state: normalizeStates(geo?.iso_country || '', geo?.iso_state || ''),
        gender: quiz_answers?.gender ? quiz_answers?.gender[0] : null,
      },
      user_ip || '',
    );

    logEvent({
      category: `${question.label}${
        question.optionsLabel ? ` - ${question.optionsLabel}` : ''
      }`,
      label: String(answer.label),
    });

    delete answer.label;
    setStep(step + 1);
    dispatch(updateQuizAnswers(answer));

    setQuestionChanged(false);

    setSelectedOptions([]);

    if (questions.length - 1 === step) {
      return goToCalculating();
    }

    goToQuizQuestion();

    setQuestionChanged(true);
    setSingleChange(true);
  };

  const handleSingleQuestion = (option: QuestionOption) => {
    const answer: QuizAnswer = {
      [String(question.key)]: option.value,
      label: option.label,
    };

    setSelectedOptions([option]);

    if (!singleChanged) {
      return;
    }
    setSingleChange(false);

    setTimeout(() => {
      handleNextQuestion(answer);
    }, 50);
  };

  const handleSliderQuestion = (option: { value: string; label: string }) => {
    const answer = {
      [String(question.key)]: option.value,
      label: option.label,
    };

    setSelectedOptions([option]);

    if (!singleChanged) {
      return;
    }
    setSingleChange(false);

    handleNextQuestion(answer);
  };

  const handleBackClick = () => {
    setStep(step - 1);

    const params = new URLSearchParams(window.location.search);
    let qParam = parseInt(params.get('q'));

    if (qParam && qParam > 2) {
      qParam -= 1;
      params.set('q', qParam.toString());
    }
    window.history.back();
  };

  const handleSkip = () => {
    setStep(step + 1);

    if (questions.length - 1 === step) {
      return goToCalculating();
    }

    goToQuizQuestion();

    setQuestionChanged(true);
    setSingleChange(true);
  };

  const handleInsetQuestionState = (key: string, value: string) => {
    const answer: QuizAnswer = {
      [key]: value,
    };

    dispatch(updateQuizAnswers(answer));
  };

  const InsetQuestionContinue = (key: string, value: string) => {
    const answer: QuizAnswer = {
      [key]: value,
    };

    handleNextQuestion(answer);
  };

  const handleMultipleQuestion = (
    option: QuestionOption,
    maxSelection?: number,
  ) => {
    const updatedSelectedOptions = () => {
      const foundOption = selectedOptions.find(
        selectedOption => selectedOption.value === option.value,
      );

      const maxSelectionLimit = maxSelection
        ? selectedOptions.length < maxSelection
        : true;

      if (!foundOption && maxSelectionLimit) {
        return [...selectedOptions, option];
      }

      return selectedOptions.filter(
        selectedOption => selectedOption.value !== option.value,
      );
    };

    setSelectedOptions(updatedSelectedOptions());
  };

  const handleScaleQuestion = (value: string) => {
    const answer: QuizAnswer = {
      [String(question.key)]: value,
    };

    handleNextQuestion(answer);
  };

  const handleInputQuestion = (value: string) => {
    const answer: QuizAnswer = {
      [String(question.key)]: value,
    };

    handleNextQuestion(answer);
  };
  const handleContinue = () => {
    const values = selectedOptions
      .map(selectedOption => selectedOption.value)
      .join('|');

    const answer: QuizAnswer = {
      [String(question.key)]: values,
      label: values,
    };

    handleNextQuestion(answer);
  };

  const handleAdditionalQuestion = () => {
    Tracking.virtualPageView(
      {
        city: encodeURIComponent(
          geo?.city?.toLowerCase().replace(/[^a-z0-9]/g, '') || '',
        ),
        country: geo?.iso_country?.toLowerCase(),
        state: normalizeStates(geo?.iso_country || '', geo?.iso_state || ''),
        pageUrl: pageUrl,
        pageTitle: window.location.pathname,
        location: geo?.iso_country?.toLocaleLowerCase(),
      },
      {
        city: encodeURIComponent(
          geo?.city?.toLowerCase().replace(/[^a-z0-9]/g, '') || '',
        ),
        country: geo?.iso_country?.toLowerCase(),
        state: normalizeStates(geo?.iso_country || '', geo?.iso_state || ''),
        gender: quiz_answers?.gender ? quiz_answers?.gender[0] : null,
      },
      user_ip || '',
    );
    if (question.type === QuestionType.Image) {
      logEvent({
        category: `image - ${question.desktopImg}`,
      });
    }

    setQuestionChanged(false);

    setStep(step + 1);

    if (questions.length - 1 === step) {
      return goToCalculating();
    }

    goToQuizQuestion();

    setQuestionChanged(true);
  };

  const handleLandingOptionSelect = (answer: QuizAnswer) => {
    Tracking.virtualPageView(
      {
        city: encodeURIComponent(
          geo?.city?.toLowerCase().replace(/[^a-z0-9]/g, '') || '',
        ),
        country: geo?.iso_country?.toLowerCase(),
        state: normalizeStates(geo?.iso_country || '', geo?.iso_state || ''),
        pageUrl: pageUrl,
        pageTitle: window.location.pathname,
        location: geo?.iso_country?.toLocaleLowerCase(),
      },
      {
        city: encodeURIComponent(
          geo?.city?.toLowerCase().replace(/[^a-z0-9]/g, '') || '',
        ),
        country: geo?.iso_country?.toLowerCase(),
        state: normalizeStates(geo?.iso_country || '', geo?.iso_state || ''),
        gender: quiz_answers?.gender ? quiz_answers?.gender[0] : null,
      },
      user_ip || '',
    );
    logEvent({
      category: `${question.label}${
        question.optionsLabel ? ` - ${question.optionsLabel}` : ''
      }`,
      label: String(answer.label),
    });
    delete answer.label;
    dispatch(updateQuizAnswers(answer));
    setStep(step + 1);
    if (questions.length - 1 === step) {
      goToCalculating();
    } else {
      goToQuizQuestion();
    }
  };

  const renderQuestion = useMemo(() => {
    switch (question?.type) {
      case QuestionType.Single:
        return (
          <Single
            question={question}
            selectedOptions={selectedOptions}
            disabled={!questionChanged || !singleChanged}
            onSelect={handleSingleQuestion}
          />
        );
      case QuestionType.Multiple:
        return (
          <MultipleAnswer
            question={question}
            selectedOptions={selectedOptions}
            disabled={!selectedOptions.length}
            onSelect={handleMultipleQuestion}
            onContinue={handleContinue}
          />
        );
      case QuestionType.MultipleImages:
        return (
          <MultipleImage
            question={question}
            selectedOptions={selectedOptions}
            disabled={!selectedOptions.length}
            onSelect={handleMultipleQuestion}
            onContinue={handleContinue}
          />
        );
      case QuestionType.Encouragement:
        return (
          <Encouragement
            question={question}
            onContinue={handleAdditionalQuestion}
          />
        );
      case QuestionType.Testimonial:
        return (
          <Testimonial
            question={question}
            onContinue={handleAdditionalQuestion}
          />
        );
      case QuestionType.Info:
        return (
          <Info question={question} onContinue={handleAdditionalQuestion} />
        );
      case QuestionType.Image:
        return (
          <ImageQuestion
            question={question}
            onContinue={handleAdditionalQuestion}
          />
        );
      case QuestionType.InfoRow:
        return (
          <TextAndImgRow {...question} onContinue={handleAdditionalQuestion} />
        );
      case QuestionType.InfoCol:
        return (
          <TextAndImgColumn
            {...question}
            onContinue={handleAdditionalQuestion}
          />
        );
      case QuestionType.Slider:
        return (
          <SliderQuestion
            question={question}
            onContinue={handleSliderQuestion}
          />
        );
      case QuestionType.Scale:
        return <Scale question={question} onContinue={handleScaleQuestion} />;
      case QuestionType.Input:
        return (
          <QuizInput
            question={question}
            onContinue={handleInputQuestion}
            handleSkip={handleSkip}
          />
        );
      case QuestionType.PreviousMultiple:
        return (
          <PreviousMultiple
            question={question}
            onContinue={handleAdditionalQuestion}
          />
        );
      case QuestionType.Insert:
        return (
          <Insert
            question={question}
            handleSkip={handleSkip}
            handleInsertQuestion={handleInsetQuestionState}
            onContinue={InsetQuestionContinue}
            handleBack={handleBackClick}
            prevStep={prevStep}
            currentStep={step}
          />
        );
      case QuestionType.Cost:
        return (
          <Cost question={question} onContinue={handleAdditionalQuestion} />
        );
      default:
        return null;
    }
  }, [
    InsetQuestionContinue,
    handleAdditionalQuestion,
    handleBackClick,
    handleContinue,
    handleInputQuestion,
    handleInsetQuestionState,
    handleMultipleQuestion,
    handleScaleQuestion,
    handleSingleQuestion,
    handleSkip,
    handleSliderQuestion,
    prevStep,
    question,
    questionChanged,
    selectedOptions,
    singleChanged,
    step,
  ]);

  if (!questions?.length) {
    return <LoadingPage />;
  }

  return (
    <>
      <Seo />
      {(question?.type === QuestionType.Landing ||
        question?.type === QuestionType.Landing2) &&
      questions[0]?.bfBanner ? (
        <BlackFridayBanner
          {...questions[0].bfBanner}
          countdownDiffInMS={countdownDiffInMS}
        />
      ) : null}
      {question?.type === QuestionType.Landing ? (
        <Landing
          question={question}
          onOptionSelect={handleLandingOptionSelect}
        />
      ) : question?.type === QuestionType.Landing2 ? (
        <Landing2
          question={question}
          onOptionSelect={handleLandingOptionSelect}
        />
      ) : (
        <>
          <HeaderContainer>
            <Header
              logoVariant="center"
              countTotal={questions.length}
              count={step + 1}
              onBack={handleBackClick}
              sticky={false}
            />

            <Progress
              step={step}
              questionsLength={questions.length}
              onBackClick={handleBackClick}
            />
          </HeaderContainer>
          <PageContainer>
            {questions[0]?.bfBanner ? (
              <BlackFridayBanner
                {...questions[0].bfBanner}
                countdownDiffInMS={countdownDiffInMS}
              />
            ) : null}
            <ContentContainer
              ref={contentContainerRef}
              questionChanged={questionChanged}
              key={step}
            >
              {renderQuestion}
            </ContentContainer>
          </PageContainer>
        </>
      )}
    </>
  );
};

export default Quiz;
